import { Stack } from '@mui/material';
import { useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { Loader } from '@/components/layout/loading';

import { SelectedItemsBody } from './selectedItemsBody';
import { SelectedItemsHeader } from './selectedItemsHeader';

export const SelectionPanel = <T,>({
  isLoading,
  drawerProps,
  children,
}: React.PropsWithChildren<{
  isLoading: boolean;
  drawerProps: Omit<SelectedItemsDrawerProps<T>, 'expanded' | 'setExpanded'>;
}>) => {
  const [expanded, setExpanded] = useState(false);
  const showDrawer = drawerProps.items.length > 0;

  if (isLoading) {
    return (
      <Stack className='h-[220px]'>
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack
      className='max-h-[480px] overflow-hidden'
      sx={{ paddingBottom: showDrawer ? '60px' : 0 }}
    >
      {children}
      {/* backdrop when drawer is open */}
      <Stack
        className='absolute top-0 left-0 w-full h-full'
        sx={{
          background: 'rgba(39, 39, 66, 0.20)',
          display: expanded ? 'block' : 'none',
          zIndex: 1,
        }}
      />
      {showDrawer && (
        <SelectedItemsDrawer
          {...drawerProps}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      )}
    </Stack>
  );
};

interface SelectedItemsDrawerProps<T> {
  entityType: string;
  items: T[];
  onRemove: (item: T) => void;
  renderItem: (item: T) => React.ReactNode;
  itemKey: (item: T) => string;
  clearAll: () => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

const SelectedItemsDrawer = <T,>({
  entityType,
  items,
  renderItem,
  itemKey,
  onRemove,
  clearAll,
  expanded,
  setExpanded,
}: SelectedItemsDrawerProps<T>) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={items.length ? () => setExpanded(!expanded) : undefined}
      className='p-0 m-0 flex flex-col absolute bottom-0 left-0 right-0'
      sx={{
        zIndex: 2,
        maxHeight: 'calc(100% - 60px)',
        backgroundColor: 'var(--mui-palette-background-paper)',

        '& .MuiCollapse-root': {
          backgroundColor: 'var(--mui-palette-background-paper)',
          position: 'relative',
          overflow: 'auto',
        },
      }}
    >
      <AccordionSummary
        className='min-h-[64px] h-[64px] px-[24px]'
        sx={{
          boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.10)',
        }}
      >
        <SelectedItemsHeader
          title={`${items.length} ${items.length === 1 ? entityType : `${entityType}${entityType.endsWith('s') ? 'es' : 's'}`} selected`}
          clearAll={() => {
            clearAll();
            setExpanded(false);
          }}
        />
      </AccordionSummary>
      <AccordionDetails className='p-0 overflow-auto'>
        <SelectedItemsBody
          items={items}
          renderItem={renderItem}
          itemKey={itemKey}
          onRemove={(item) => {
            if (items.length === 1) {
              setExpanded(false);
            }
            onRemove(item);
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};
